import './App.css';
import cdLogo from './Pics/CD Logo.png';
import { LicenseInfo } from '@mui/x-license-pro';
import { DataGridPro, GridColumnOrderChangeParams, GridColumnResizeParams, GridToolbar, useGridApiRef } from '@mui/x-data-grid-pro';
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  changeProductCode,
  createRecord,
  deactivateRecord,
  fetchNextProductCode,
  fetchProducts,
  fetchTableMetadata,
  forceSync,
  setAuthToken,
  updateRecord,
} from './apiService';
import React from 'react';
import DetailPanelForm from './DetailPanelForm';
import { Avatar, IconButton, Box, CircularProgress, Switch, FormControlLabel, TextField, Button, Snackbar, Alert, InputAdornment } from '@mui/material';
import { getTenantIdFromUrl } from './utils';
import LoginPopup from './LoginPopup';
import AddIcon from '@mui/icons-material/Add';
import UserMenu from './UserMenu';
import EditIcon from '@mui/icons-material/Edit';
import AddRowPopup from './AddRowPopup';
import SyncIcon from '@mui/icons-material/Sync';
import ProductSelect from './ProductSelect';
import { withProductOptions } from './productOptions';
import ChangeCodeDialog from './ChangeCodeDialog';
import BulkCodeChangePage from './BulkCodeChangePage';

const MultilineTextCell = ({ value }) => (
  <div style={{ whiteSpace: 'pre-wrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
    {value && value.split(/\r\n|\r|\n/).map((line, index, array) => (
      <React.Fragment key={index}>
        {line}
        {index < array.length - 1 && <br />}
      </React.Fragment>
    ))}
  </div>
);

const MultilineEditInputCell = ({ id, value, field, api }) => {
  const handleChange = (event) => {
    const newValue = event.target.value;
    api.setEditCellValue({ id, field, value: newValue });
  };

  return (
    <textarea
      value={value}
      onChange={handleChange}
      style={{ width: '100%', height: '100%', resize: 'none', whiteSpace: 'pre-wrap' }}
    />
  );
};

const debugLog = (...args) => {
  if (process.env.REACT_APP_DEBUG_MODE === 'true') {
    console.log(...args);
  }
};

const debugError = (...args) => {
  if (process.env.REACT_APP_DEBUG_MODE === 'true') {
    console.error(...args);
  }
};

const CustomToolbar = ({ onAddNewRow }) => {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px' }}>
      <div>
        <GridToolbar />
      </div>
      <div>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={onAddNewRow}
        style={{ marginRight: '8px' }}
      >
        Add Product
      </Button>
      </div>
    </div>
  );
};

const CustomFooter = ({ showActive, setShowActive }) => {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', padding: '8px' }}>
      <FormControlLabel
        control={
          <Switch
            checked={showActive}
            onChange={(e) => setShowActive(e.target.checked)}
            color="primary"
          />
        }
        label="Active Products"
      />
    </div>
  );
};

const Notification = ({ message, type, onClose }) => {
  return <Snackbar open={!!message} autoHideDuration={5000} onClose={onClose}>
    <Alert onClose={onClose} severity={type} sx={{ width: '100%' }}>
      {message}
    </Alert>
  </Snackbar>
};

// Apply the MUI X Pro license key
LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_X_PRO_LICENSE_KEY);

function App() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState({});
  const [columns, setColumns] = useState([]);
  const dataColumns = useMemo(() => withProductOptions(columns, rows), [columns, rows]);
  const apiRef = useGridApiRef();
  const [pageSize, setPageSize] = useState(5);
  const [recordCount, setRecordCount] = useState(0);
  const [tableName, setTableName] = useState('');
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const [notification, setNotification] = useState(null);
  const [isAddRowPopupOpen, setIsAddRowPopupOpen] = useState(false);
  const [primaryField, setPrimaryField] = useState('');
  const [tableMetadata, setTableMetadata] = useState(null);
  const [showActive, setShowActive] = useState(true);
  const [tenantId, setTenantId] = useState(null);
  const [isLoginPopupOpen, setIsLoginPopupOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [tenantInfo, setTenantInfo] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [ignoredFields, setIgnoredFields] = useState([
    'SYNC_TIME',
    // Add any other fields you want to ignore here
  ]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [changeCodeProduct, setChangeCodeProduct] = useState(null);
  const [activeView, setActiveView] = useState('products');

  const toggleAddRowPopup = () => {
    setIsAddRowPopupOpen(!isAddRowPopupOpen);
  };

  const handleAvatarClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    debugLog('Columns:', columns);
    debugLog('Editable Fields:', getEditableFields());
  }, [columns]);

  useEffect(() => {
    debugLog('AddRowPopup props:', {
      columns,
      editableFields: getEditableFields(),
      primaryField,
      tableMetadata
    });
  }, [columns, primaryField, tableMetadata]);

  // Add a new useEffect to log the state of the primary field in rows
  useEffect(() => {
    if (rows.length > 0 && primaryField) {
      debugLog('Primary field values in rows:', rows.map(row => row[primaryField]));
    }
  }, [rows, primaryField]);

  useEffect(() => {
    if (columns.length > 0) {
      saveSettings(columns, columnVisibilityModel);
    }
  }, [columns, columnVisibilityModel]);

  const filteredRows = showActive ? rows.filter(row => !row.inactive) : rows;

  const LOCAL_STORAGE_KEY = 'dataGridSettings';
  const AUTH_STORAGE_KEY = 'authStatus';

  useEffect(() => {
    const extractedTenantId = getTenantIdFromUrl();
    if (extractedTenantId) {
      setTenantId(extractedTenantId);
    } else {
      setIsLoginPopupOpen(true);
    }
  }, []);

  useEffect(() => {
    // Only check localStorage for remembered login
    const localData = localStorage.getItem(AUTH_STORAGE_KEY);
    if (localData) {
      const authData = JSON.parse(localData);
      // Check if the stored data is still valid
      if (authData.token && new Date(authData.expiry) > new Date()) {
        setAuthToken(authData.token);
        setIsAuthenticated(true);
        setUsername(authData.username);
        setTenantInfo(authData.tenantInfo);
        setTableName(authData.tableName); // Add this line to restore tableName
        setIsLoginPopupOpen(false);
      } else {
        // Clear expired auth data
        localStorage.removeItem(AUTH_STORAGE_KEY);
        setIsLoginPopupOpen(true);
      }
    } else {
      setIsLoginPopupOpen(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && username && tenantInfo?.Table_Name) {
      loadData(tenantInfo.Table_Name, username)
        .catch((error) => {
          console.error('Error loading data:', error);
          setNotification({ message: 'Error loading data: ' + error.message, type: 'error' });
        });
    }
  }, [isAuthenticated, username]);

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUsername('');
    setPassword('');
    setTenantInfo(null);
    setAuthToken(null);
    // Clear both storages
    localStorage.removeItem(AUTH_STORAGE_KEY);
    setIsDataLoaded(false);
    setIsLoginPopupOpen(true);
    setAnchorEl(null);
    setActiveView('products');
  };

  const handleLogin = async (userData, rememberMe) => {
    try {
      setUsername(userData.user_name);
      setAuthToken(userData.token);
      setTableName(userData.table_name);
      setTenantInfo({
        User_Name: userData.user_name,
        Table_Name: userData.table_name,
        Tenant_Name: userData.tenant_name,
        ...userData.settings
      });

      setIsAuthenticated(true);
      setIsLoginPopupOpen(false);

      // Only store auth data if remember me is checked
      if (rememberMe) {
        const authData = {
          username: userData.user_name,
          token: userData.token,
          tableName: userData.table_name,
          tenantInfo: {
            User_Name: userData.user_name,
            Table_Name: userData.table_name,
            Tenant_Name: userData.tenant_name,
            ...userData.settings
          },
          expiry: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // 30 days
        };
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(authData));
      }

      try {
        await loadData(userData.table_name, userData.user_name);
      } catch (error) {
        console.error('Error loading data:', error);
        setNotification({ message: 'Error loading data: ' + error.message, type: 'error' });
      }

      return true;
    } catch (error) {
      console.error('Error during login:', error);
      return false;
    }
  };

  const handleFormSubmit = async (data) => {
    try {
      const processedData = Object.keys(data).reduce((acc, key) => {
        const column = columns.find(col => col.field === key);
        if (column && column.type === 'boolean') {
          acc[key] = !!data[key];
        } else {
          acc[key] = data[key];
        }
        return acc;
      }, {});

      const updatedRecord = await handleProcessRowUpdate(processedData);
      if (updatedRecord) {
        setNotification({ message: 'Product updated successfully', type: 'success' });
      }
      // No else needed as handleProcessRowUpdate will set error notification
    } catch (error) {
      setNotification({ message: `Failed to update product: ${error.message}`, type: 'error' });
    }
  };

  const getEditableFields = useCallback(() => {
    return columns
      .filter(column => column.editable && column.field !== 'id')
      .map(column => column.field);
  }, [columns]);

  const mapAirtableTypeToDataGridType = (airtableType) => {
    switch (airtableType) {
      case 'number':
        return 'number';
      case 'date':
      case 'dateTime':
        return 'date';
      case 'checkbox':
        return 'boolean';
      case 'multipleSelects':
      case 'multipleAttachments':
        return 'array';
      case 'singleSelect':
        return 'singleSelect';
      case 'multilineText':
        return 'multilineText';
      case 'formula':
      case 'rollup':
      case 'count':
      case 'computation':
      case 'autoNumber':
      case 'createdTime':
      case 'lastModifiedTime':
        return 'readonly';
      default:
        return 'string';
    }
  };

  const renderBooleanCell = (params) => {
    if (params.value === true) {
      return <span style={{ color: 'green' }}>✓</span>;
    } else if (params.value === false) {
      return <span style={{ color: 'red' }}>✗</span>;
    } else {
      return '';
    }
  };

  const isFieldEditable = (airtableType) => {
    const nonEditableTypes = ['formula', 'rollup', 'count', 'computation', 'autoNumber', 'createdTime', 'lastModifiedTime'];
    return !nonEditableTypes.includes(airtableType);
  };

  const handleColumnOrderChange = (params) => {
    debugLog('Column order change triggered', params);
  
    // Validate params
    if (!params || typeof params.targetIndex !== 'number' || typeof params.oldIndex !== 'number') {
      debugError('Invalid column order change params:', params);
      return;
    }
  
    // Adjust indexes to be 0-based
    const oldIndex = params.oldIndex - 1;
    const targetIndex = params.targetIndex - 1;
  
    debugLog('Adjusted Old Index (0-based):', oldIndex, 'Adjusted Target Index (0-based):', targetIndex);
  
    // Create a copy of the columns array
    const newColumns = [...columns];
    debugLog('Initial newColumns array:', newColumns.map(col => col.field));
  
    // Remove the column from the old index
    const [reorderedColumn] = newColumns.splice(oldIndex, 1);
    debugLog('Reordered column:', reorderedColumn.field, 'Remaining columns after removal:', newColumns.map(col => col.field));
  
    // Insert the column into the target index
    newColumns.splice(targetIndex, 0, reorderedColumn);
    debugLog('Columns after insertion at target index:', newColumns.map(col => col.field));
  
    // Update state
    setColumns(newColumns);
    debugLog('New column order before saving:', newColumns.map((col, index) => ({ field: col.field, order: index })));
  
    // Save the updated settings
    saveSettings(newColumns, columnVisibilityModel);
    debugLog('Column order changed and saved:', newColumns.map(col => col.field));
  };

  const handleColumnResize = (params) => {
    const resizedColumns = columns.map((col) => 
      col.field === params.colDef.field ? { ...col, width: params.width } : col
    );
    setColumns(resizedColumns);
    saveSettings(resizedColumns, columnVisibilityModel);
    debugLog('Column resized:', resizedColumns);
  };

  const loadData = useCallback(async (tableName, currentUsername) => { 
    try {
      setLoading(true);
      setIsLoading(true);
      
      debugLog('Loading data for table:', tableName);
      debugLog('Current username:', currentUsername);
      
      if (!tableName) {
        throw new Error('Table name is required');
      }

      // Get metadata first
      debugLog('Fetching metadata for table:', tableName);
      const metadata = await fetchTableMetadata(tableName);
      debugLog('Metadata response:', metadata);

      if (!metadata || !metadata.columns) {
        throw new Error('Invalid metadata structure received');
      }

      // Then get records
      debugLog('Fetching records for table:', tableName);
      const records = await fetchProducts();
      debugLog('Records received:', records);

      if (!Array.isArray(records)) {
        throw new Error('Invalid records received from API');
      }

      setRecordCount(records.length);
      setRows(records);
 
      const dynamicColumns = metadata.columns.map(field => ({
        field: field.name,
        headerName: field.name.split('_').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' '), // Convert snake_case to Title Case
        width: 150,
        editable: field.editable,
        type: field.type, // Use the type directly from metadata
        options: field.options,
        valueFormatter: (params) => {
          // Format values based on type
          switch (field.type) {
            case 'datetime':
              return params.value ? new Date(params.value).toLocaleString() : '';
            case 'boolean':
              return params.value ? '✓' : '✗';
            case 'multipleSelects':
              return Array.isArray(params.value) ? params.value.join(', ') : '';
            case 'singleSelect':
              return params.value || '';
            default:
              return params.value;
          }
        },
        renderCell: field.type === 'multilineText' 
          ? (params) => <MultilineTextCell value={params.value} />
          : field.type === 'boolean'
          ? (params) => params.value ? <span style={{ color: 'green' }}>✓</span> : <span style={{ color: 'red' }}>✗</span>
          : field.type === 'singleSelect'
          ? (params) => <span>{params.value || ''}</span>
          : field.type === 'multipleSelects'
          ? (params) => <span>{Array.isArray(params.value) ? params.value.join(', ') : ''}</span>
          : undefined,
        renderEditCell: field.type === 'multilineText' 
          ? (params) => <MultilineEditInputCell {...params} />
          : ['singleSelect', 'multipleSelects'].includes(field.type)
          ? (params) => (
              <ProductSelect
                options={params.colDef.options || []}
                multiple={params.colDef.type === 'multipleSelects'}
                value={params.value}
                label={params.colDef.headerName}
                onChange={value => params.api.setEditCellValue({
                  id: params.id, field: params.field, value,
                })}
                size="small"
              />
            )
          : undefined,
      }));

      // Set the primary field from metadata
      setPrimaryField(metadata.primaryField || '');
      
      debugLog('Columns configured:', dynamicColumns);
      setColumns(dynamicColumns);
      setIsDataLoaded(true);

    } catch (error) {
      debugError('Error loading data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
      setIsLoading(false);
    }
  }, []);

  const handleProcessRowUpdate = async (newRow) => {
    try {
      debugLog('Starting row update process:', { newRow, tableName });
      
      if (!tableName) {
        setNotification({ message: 'Table name is not available. Please reload the page.', type: 'error' });
        return null;
      }

      const { id, ...fields } = newRow;
      debugLog('Processing fields for update:', { id, fields });

      const filteredFields = Object.fromEntries(
        Object.entries(fields).filter(([key, value]) => {
          const column = columns.find(col => col.field === key);
          const keep = column && column.editable && !ignoredFields.includes(key);
          if (!keep) {
            debugLog('Filtering out field:', { key, reason: !column ? 'column not found' : !column.editable ? 'not editable' : 'ignored field' });
          }
          return keep;
        })
      );
      debugLog('Filtered fields:', filteredFields);

      const processedFields = Object.fromEntries(
        Object.entries(filteredFields).map(([key, value]) => {
          const column = columns.find(col => col.field === key);
          let processedValue = value;

          if (column) {
            if (column.type === 'number') {
              processedValue = value === '' ? null : Number(value);
              debugLog('Processing number field:', { key, originalValue: value, processedValue });
            }
            else if (column.type === 'multilineText') {
              debugLog('Preserving multiline text field:', { key, value });
            }
            else if (column.type === 'multipleSelects') {
              // Ensure value is always an array
              processedValue = Array.isArray(value) ? value : (value ? [value] : []);
              debugLog('Processing multi-select field:', { key, originalValue: value, processedValue });
            }
          }

          return [key, processedValue];
        })
      );
      debugLog('Processed fields for update:', processedFields);

      // Wait for the update to complete and get the updated record
      debugLog('Sending update request to server...');
      const updatedRecord = await updateRecord(tableName, id, processedFields, ignoredFields);
      debugLog('Received updated record from server:', updatedRecord);

      // Only update local state if we get here (no error was thrown)
      debugLog('Updating local state with new record');
      setRows(currentRows => currentRows.map(row => {
        if (row.id === newRow.id) {
          debugLog('Updating row:', { oldRow: row, newData: updatedRecord });
          return { ...row, ...updatedRecord };
        }
        return row;
      }));
      
      setNotification({ message: 'Record updated successfully', type: 'success' });
      return updatedRecord; // Return the server's version of the record
    } catch (error) {
      debugError('Error in handleProcessRowUpdate:', error);
      debugError('Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response?.data
      });
      
      // Show the specific error message from the server or a user-friendly message
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update record. Please try again.';
      setNotification({ message: errorMessage, type: 'error' });
      
      return null; // Return null to indicate update failed
    }
  };

  const handleAddRow = async (data, codeOptions = null) => {
    debugLog('Adding new product:', data);
    const filteredData = {};
    for (const [key, value] of Object.entries(data)) {
      if (value !== null && value !== '') {
        filteredData[key] = typeof value === 'string' ? value.trim() : value;
      }
    }
    debugLog('Filtered data to be sent:', filteredData);
    
    // Typed codes must not be empty; the server builds generated codes
    if (!codeOptions && !filteredData[primaryField]) {
      const primaryFieldName = columns.find(col => col.field === primaryField)?.headerName || 'Primary field';
      throw new Error(`${primaryFieldName} cannot be empty`);
    }

    const newRecord = await createRecord(tableName, filteredData, codeOptions?.codeNumber);
    setRows((prevRows) => [...prevRows, newRecord]);
    setNotification({ message: `New product ${newRecord.code} added`, type: 'success' });
  };

  const handleDeleteRow = async (id) => {
    try {
      const deactivated = await deactivateRecord(tableName, id);
      setRows((prevRows) => prevRows.map(row => row.id === id ? deactivated : row));
      setNotification({ message: 'Product deactivated successfully', type: 'success' });
    } catch (error) {
      setNotification({ message: `Failed to delete product: ${error.message}`, type: 'error' });
    }
  };

  const handleForceSync = async () => {
    try {
      setIsSyncing(true);
      setNotification({ message: 'Queuing all products for store sync...', type: 'info' });
      const result = await forceSync();
      setNotification({ message: `${result.count} products queued for sync`, type: 'success' });
    } catch (error) {
      setNotification({ message: `Sync failed: ${error.message}`, type: 'error' });
    } finally {
      setIsSyncing(false);
      setAnchorEl(null); // Close the user menu
    }
  };

  const handleChangeCode = async (product, newCode) => {
    const result = await changeProductCode(product.id, newCode);
    setRows((currentRows) => currentRows.map((row) => (
      row.id === product.id ? result.product : row
    )));
    setNotification({ message: `Code changed from ${product.code} to ${result.product.code}`, type: 'success' });
  };

  const saveSettings = (columns, visibilityModel) => {
    debugLog('saveSettings called with columns:', columns.map(col => ({ field: col.field, order: columns.indexOf(col) })));
    const settings = {
      columns: columns.map((col, index) => ({
        field: col.field,
        width: col.width,
        order: index, // Save the actual index
        hide: col.hide
      })),
      columnVisibilityModel: visibilityModel,
    };
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(settings));
    debugLog('Settings saved:', settings);
  };

  const onColumnVisibilityModelChange = (newModel) => {
    setColumnVisibilityModel(newModel);

    const updatedColumns = columns.map((col) => ({
      ...col,
      hide: newModel[col.field] === false,
    }));

    saveSettings(updatedColumns, newModel);
    debugLog('Column visibility model changed:', newModel); // Debug log
  };

  return (
    <div className={`App ${(isAddRowPopupOpen || isLoginPopupOpen) ? 'blurred' : ''}`}>
      <LoginPopup
        open={isLoginPopupOpen}
        onClose={() => setIsLoginPopupOpen(false)}
        onLogin={handleLogin}
        isLoading={isLoading}
      />
      {error && (
        <div style={{ color: 'red', textAlign: 'center', marginTop: '20px' }}>
          {error}
        </div>
      )}
      <header className="App-header">
        <div className="logo-wrapper">
          <img src={cdLogo} alt="CD Logo" className="App-logo" />
          {isAuthenticated && tenantInfo && (
            <>
              <IconButton
                onClick={handleAvatarClick}
                style={{ position: 'absolute', right: '16px', height: '95%' }}
              >
                <Avatar style={{ backgroundColor: '#1976d2', height: '80%', width: 'auto', aspectRatio: '1 / 1', fontSize: 'calc(0.7 * 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                  {tenantInfo.Tenant_Name.charAt(0)}
                </Avatar>
              </IconButton>
              {Boolean(anchorEl) && (
                <UserMenu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleCloseUserMenu}
                  onLogout={handleLogout}
                  onForceSync={handleForceSync}
                  onBulkCodeChanges={() => setActiveView('bulkCodeChanges')}
                  username={username}
                />
              )}
            </>
          )}
        </div>
        <div className="text-wrapper">
          <h1>Concession Product Central<sup>©</sup></h1>
        </div>
      </header>
      <Notification message={notification?.message} type={notification?.type} onClose={() => setNotification(null)} />
      {isAuthenticated && !isDataLoaded && <CircularProgress />}
      {isAuthenticated && isDataLoaded && (
      <main className="App-main" style={{ position: 'relative' }}>
        {activeView === 'bulkCodeChanges' ? (
          <BulkCodeChangePage
            onBack={() => setActiveView('products')}
            onProductsChanged={() => loadData(tableName, username)}
            onNotify={(message, type) => setNotification({ message, type })}
          />
        ) : (
        <div style={{ display: 'flex', flexDirection: 'column-reverse', height: 'calc(100vh - 65px)' }}>
          <div style={{ flexGrow: 1, width: '100%', height: 'calc(100% - 52px)' }}>
            {columns.length > 0 ? (
              <DataGridPro
                apiRef={apiRef}
                rows={filteredRows}
                columns={dataColumns}
                processRowUpdate={handleProcessRowUpdate}
                loading={loading}
                rowsPerPageOptions={[5, 10, 25, 50]}
                disableColumnReorder={false}
                disableColumnResize={false}
                autoHeight={false}
                sx={{
                  height: '100%',
                  '& .MuiDataGrid-main': { overflow: 'hidden' },
                  '& .MuiDataGrid-virtualScroller': { overflow: 'auto' },
                  '& .MuiDataGrid-footerContainer': {
                    backgroundColor: 'blue',
                    color: 'white',
                  },
                }}
                onColumnOrderChange={handleColumnOrderChange}
                onColumnResize={handleColumnResize}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={onColumnVisibilityModelChange}
                components={{
                  Toolbar: CustomToolbar,
                  Footer: CustomFooter,
                }}
                componentsProps={{
                  toolbar: { onAddNewRow: toggleAddRowPopup },
                  footer: { showActive, setShowActive },
                }}
                getDetailPanelContent={({ row }) => (
                  <DetailPanelForm
                    row={row}
                    onSubmit={handleFormSubmit}
                    columns={dataColumns.map(col => ({
                      ...col,
                      type: col.type === 'multilineText' ? 'multilineText' : col.type,
                      options: col.options
                    }))}
                    editableFields={getEditableFields()}
                    primaryField={primaryField}
                    onChangeCode={setChangeCodeProduct}
                  />
                )}
                getDetailPanelHeight={() => 'auto'}
              />
            ) : (
              <div>Loading products...</div>
            )}
          </div>
        </div>
        )}
      </main>
      )}
      {isSyncing && <CircularProgress style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />}
      <AddRowPopup
        open={isAddRowPopupOpen}
        onClose={toggleAddRowPopup}
        onSubmit={handleAddRow}
        columns={dataColumns}
        editableFields={getEditableFields()}
        primaryField={primaryField}
        loadNextCode={fetchNextProductCode}
      />
      <ChangeCodeDialog
        open={Boolean(changeCodeProduct)}
        product={changeCodeProduct}
        onClose={() => setChangeCodeProduct(null)}
        onConfirm={handleChangeCode}
      />
    </div>
  );
}

export default App;
